/**
 * SolutionsListPage — Lists all solutions (original + hybrid) for a workshop instance.
 * Route: /solutions?kid={instanceId}
 *
 * Reuses the compact card pattern from ReportSolutions/ReportHybridSolutions.
 */
import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { ArrowRight, ArrowLeft, Zap, Target, Lightbulb, BookOpen, Merge, Printer } from 'lucide-react';
import { API_ENDPOINTS } from '../config/api';
import { EVALUATION_TAG_META } from '@kuutio/shared/evaluationTags.js';
import { getCompositeScore, isTagBasedEvaluation } from '../components/Report/SolutionAnalysis';
import { useTranslation } from '../hooks/useTranslation.jsx';
import {
  LoadingContainer, Spinner, LoadingText, ErrorContainer,
} from '../components/Report/reportStyles';

// ─── Layout ──────────────────────────────────────────────────────────────────

const PageWrap = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  @media print { background: #fff; }
`;

const Header = styled.header`
  background: linear-gradient(135deg, #1a1a2e 0%, #2d2d44 100%);
  color: #fff;
  padding: 60px 32px 48px;
  @media print {
    background: #1a1a2e !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    padding: 24px 0;
  }
`;

const HeaderContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  @media print { margin-bottom: 12px; }
`;

const BackLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 6px;
  color: rgba(255,255,255,0.7);
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  &:hover { color: #fff; }
  @media print { display: none; }
`;

const PrintButton = styled.button`
  background: rgba(255,255,255,0.15);
  color: #fff;
  border: 1px solid rgba(255,255,255,0.3);
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  &:hover { background: rgba(255,255,255,0.25); }
  @media print { display: none; }
`;

const WorkshopLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: rgba(255,255,255,0.5);
  letter-spacing: 2px;
  text-transform: uppercase;
  margin-bottom: 8px;
`;

const Title = styled.h1`
  font-size: 34px;
  font-weight: 800;
  margin: 0 0 8px;
  line-height: 1.25;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: rgba(255,255,255,0.7);
  margin: 0;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 20px;
`;

const StatBadge = styled.div`
  padding: 6px 14px;
  border-radius: 10px;
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.2);
  font-size: 14px;
  font-weight: 600;
  span { font-weight: 400; opacity: 0.7; margin-left: 4px; }
`;

// ─── Main ────────────────────────────────────────────────────────────────────

const Main = styled.main`
  max-width: 900px;
  margin: 0 auto;
  padding: 32px 32px 64px;
  @media print { padding: 16px 0; }
`;

const SectionLabel = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e5e7eb;
`;

// ─── Cards (shared between originals and hybrids) ────────────────────────────

const CardGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 40px;
`;

const SolutionCard = styled(Link)`
  display: block;
  text-decoration: none;
  background: ${p => p.$hybrid ? '#faf8ff' : '#fff'};
  border: 1px solid ${p => p.$hybrid ? '#e9e5f5' : '#e5e7eb'};
  border-left: 4px solid ${p => p.$color};
  border-radius: 0 12px 12px 0;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  transition: box-shadow 0.15s, transform 0.15s;
  &:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.08); transform: translateY(-1px); }
  @media print { box-shadow: none; &:hover { transform: none; } }
`;

const CardContent = styled.div`
  padding: 16px 20px;
`;

const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const CardTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0;
  flex: 1;
  line-height: 1.3;
`;

const TotalScore = styled.div`
  font-size: 18px;
  font-weight: 800;
  color: ${p => p.$score >= 3 ? '#059669' : '#f59e0b'};
  white-space: nowrap;
  flex-shrink: 0;
  span { font-size: 12px; font-weight: 500; color: #9ca3af; }
`;

const HybridBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 8px;
  color: #7c3aed;
  background: #ede9fe;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 4px;
`;

const Description = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: #555;
  margin: 8px 0 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 10px;
`;

const ScorePills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const ScorePill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 10px;
  background: ${p => p.$bg || '#f3f4f6'};
  color: ${p => p.$color || '#6b7280'};
`;

const ReadMore = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 600;
  color: #3b82f6;
  white-space: nowrap;
  flex-shrink: 0;
`;

const Footer = styled.footer`
  text-align: center;
  padding: 24px 0;
  font-size: 13px;
  color: #9ca3af;
  border-top: 1px solid #e5e7eb;
`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const COLORS = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#14b8a6'];

const EVAL_PILLS = {
  impact: { icon: Zap, label: 'Vaik.', bg: '#f5f3ff', color: '#7c3aed' },
  feasibility: { icon: Target, label: 'Tot.', bg: '#ecfdf5', color: '#059669' },
  innovation: { icon: Lightbulb, label: 'Uutuus', bg: '#eff6ff', color: '#2563eb' },
  evidence: { icon: BookOpen, label: 'Näyttö', bg: '#fffbeb', color: '#b45309' },
};

// ─── Component ───────────────────────────────────────────────────────────────

export default function SolutionsListPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const instanceId = searchParams.get('kid') || searchParams.get('instance');

  const [solutions, setSolutions] = useState([]);
  const [hybrids, setHybrids] = useState([]);
  const [workshopTitle, setWorkshopTitle] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!instanceId) { setLoading(false); return; }
    Promise.all([
      fetch(API_ENDPOINTS.SOLUTIONS(instanceId), { credentials: 'include' }).then(r => r.ok ? r.json() : []),
      fetch(API_ENDPOINTS.SOLUTION_HYBRIDS(instanceId), { credentials: 'include' }).then(r => r.ok ? r.json() : []),
      fetch(API_ENDPOINTS.INSTANCE(instanceId)).then(r => r.ok ? r.json() : null),
    ]).then(([sols, hybs, inst]) => {
      setSolutions(Array.isArray(sols) ? sols : sols?.solutions || []);
      setHybrids(Array.isArray(hybs) ? hybs : []);
      if (inst?.title) setWorkshopTitle(inst.title);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [instanceId]);

  const sortedSolutions = useMemo(() =>
    [...solutions].sort((a, b) => (a.order_index ?? 999) - (b.order_index ?? 999)),
    [solutions]
  );

  const sortedHybrids = useMemo(() =>
    [...hybrids].sort((a, b) => (a.order_index ?? 999) - (b.order_index ?? 999)),
    [hybrids]
  );

  const avgScore = useMemo(() => {
    const all = [...solutions, ...hybrids];
    const scores = all.map(s => getCompositeScore(s.ai_analysis)).filter((x) => x !== null && x !== undefined);
    return scores.length ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1) : null;
  }, [solutions, hybrids]);

  const tagAnalyzedCount = useMemo(() =>
    [...solutions, ...hybrids].filter(s => isTagBasedEvaluation(s.ai_analysis?.evaluation)).length,
  [solutions, hybrids]);

  if (loading) return <LoadingContainer><Spinner /><LoadingText>{t('solutionsList.loading')}</LoadingText></LoadingContainer>;
  if (!instanceId) return <ErrorContainer><h2>{t('solutionsList.instanceMissingError')}</h2><p>{t('solutionsList.addInstanceUrl')}</p></ErrorContainer>;

  const reportUrl = `/raportti?kid=${instanceId}`;

  return (
    <PageWrap>
      <Header>
        <HeaderContent>
          <HeaderTop>
            <BackLink to={reportUrl}><ArrowLeft size={16} /> Raporttiin</BackLink>
            <PrintButton onClick={() => window.print()}><Printer size={18} /> Tulosta</PrintButton>
          </HeaderTop>
          <WorkshopLabel>{workshopTitle ? `Kuutio — ${workshopTitle}` : 'Kuutio'}</WorkshopLabel>
          <Title>Ratkaisuehdotukset</Title>
          <Subtitle>
            {solutions.length} ratkaisua{hybrids.length > 0 && ` + ${hybrids.length} yhdistelmää`}
          </Subtitle>
          <StatsRow>
            {avgScore && <StatBadge>{avgScore}<span>/ 5 {t('solutionsList.averageScore')}</span></StatBadge>}
            {tagAnalyzedCount > 0 && (
              <StatBadge>{tagAnalyzedCount}<span>{t('solutionsList.evidenceLabel')}</span></StatBadge>
            )}
            <StatBadge>{solutions.length + hybrids.length}<span>{t('solutionsList.total')}</span></StatBadge>
          </StatsRow>
        </HeaderContent>
      </Header>

      <Main>
        {sortedSolutions.length > 0 && (
          <>
            <SectionLabel>{t('solutionsList.groupSolutions')} ({sortedSolutions.length})</SectionLabel>
            <CardGrid>
              {sortedSolutions.map((sol, i) => {
                const score = getCompositeScore(sol.ai_analysis);
                const evaluation = sol.ai_analysis?.evaluation;
                const tagEval = isTagBasedEvaluation(evaluation);
                const title = sol.metadata?.ai_title || sol.title?.split(/[.!?\n]/)[0] || sol.title;
                const snippet = sol.description || sol.title || '';
                return (
                  <SolutionCard key={sol.id} to={`/solutions/${sol.id}`} $color={COLORS[i % COLORS.length]}>
                    <CardContent>
                      <CardTop>
                        <CardTitle>{title}</CardTitle>
                        {score !== null && !tagEval && <TotalScore $score={score}>{score} <span>/ 5</span></TotalScore>}
                      </CardTop>
                      <Description>{snippet}</Description>
                      <CardFooter>
                        <ScorePills>
                          {tagEval && evaluation.tags?.map((tag) => {
                            const meta = EVALUATION_TAG_META[tag.id] || { bgLight: '#f3f4f6', color: '#374151' };
                            const lk = `evaluationTags.${tag.id}`;
                            const lab = t(lk);
                            const display = lab === lk ? tag.id : lab;
                            return <ScorePill key={tag.id} $bg={meta.bgLight} $color={meta.color}>{display}</ScorePill>;
                          })}
                          {!tagEval && evaluation && Object.entries(EVAL_PILLS).map(([key, style]) => {
                            const data = evaluation[key];
                            if (!data?.score) return null;
                            const Icon = style.icon;
                            return <ScorePill key={key} $bg={style.bg} $color={style.color}><Icon size={11} />{style.label} {data.score}</ScorePill>;
                          })}
                        </ScorePills>
                        <ReadMore>Avaa <ArrowRight size={14} /></ReadMore>
                      </CardFooter>
                    </CardContent>
                  </SolutionCard>
                );
              })}
            </CardGrid>
          </>
        )}

        {sortedHybrids.length > 0 && (
          <>
            <SectionLabel>{t('solutionsList.hybridSolutions')} ({sortedHybrids.length})</SectionLabel>
            <CardGrid>
              {sortedHybrids.map((sol, i) => {
                const score = getCompositeScore(sol.ai_analysis);
                const evaluation = sol.ai_analysis?.evaluation;
                const tagEval = isTagBasedEvaluation(evaluation);
                const title = sol.metadata?.ai_title || sol.title;
                const snippet = sol.description || sol.hybrid_rationale || sol.title || '';
                return (
                  <SolutionCard key={sol.id} to={`/solutions/${sol.id}`} $color="#8b5cf6" $hybrid>
                    <CardContent>
                      <CardTop>
                        <div style={{ flex: 1 }}>
                          <HybridBadge><Merge size={10} /> {t('solutionsList.hybridBadge')}</HybridBadge>
                          <CardTitle>{title}</CardTitle>
                        </div>
                        {score !== null && !tagEval && <TotalScore $score={score}>{score} <span>/ 5</span></TotalScore>}
                      </CardTop>
                      <Description>{snippet}</Description>
                      <CardFooter>
                        <ScorePills>
                          {tagEval && evaluation.tags?.map((tag) => {
                            const meta = EVALUATION_TAG_META[tag.id] || { bgLight: '#f3f4f6', color: '#374151' };
                            const lk = `evaluationTags.${tag.id}`;
                            const lab = t(lk);
                            const display = lab === lk ? tag.id : lab;
                            return <ScorePill key={tag.id} $bg={meta.bgLight} $color={meta.color}>{display}</ScorePill>;
                          })}
                          {!tagEval && evaluation && Object.entries(EVAL_PILLS).map(([key, style]) => {
                            const data = evaluation[key];
                            if (!data?.score) return null;
                            const Icon = style.icon;
                            return <ScorePill key={key} $bg={style.bg} $color={style.color}><Icon size={11} />{style.label} {data.score}</ScorePill>;
                          })}
                        </ScorePills>
                        <ReadMore>Avaa <ArrowRight size={14} /></ReadMore>
                      </CardFooter>
                    </CardContent>
                  </SolutionCard>
                );
              })}
            </CardGrid>
          </>
        )}

        <Footer>{t('solutionsList.footer')}</Footer>
      </Main>
    </PageWrap>
  );
}
